import { all, takeLatest, select, call, put } from 'redux-saga/effects';
import { request } from '@wix/da-shared-react/pkg/xhr';
import { getCurrentUser } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { FEATURED_POSTS_IDS_STREAM } from '../streams/posts';
import {
  withOffset,
  selectors as baseSelectors,
} from '@wix/da-shared-react/pkg/Stream';
import { normalize } from '@wix/da-shared-react/pkg/redux/normalizr';
import { flagEnabled } from '@wix/da-shared-react/pkg/redux/flags';
import { deviationSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import { putEntities } from '@wix/da-shared-react/pkg/redux/entities/actions';
import { getDataEntityById } from '@wix/da-shared-react/pkg/redux/entities/selectors';
import { getActiveSection } from '../selectors/sections';
import { API_BASE_PATH } from '../../constants';
import { ModuleType } from '../../types/modules';
import { runModuleInitActions } from './modules';
import {
  pinPostOnFeed,
  featurePost,
  unfeaturePost,
  pinPost,
  unpinPost,
} from '../actions/feed';
import { updateModule } from '../actions/modules';
import { getModuleIdByModuleType } from '../selectors/modules';
import ddt from '@wix/da-ddt';

function* postRequest(modulePath, data = {}) {
  return yield call(request, {
    method: 'post',
    baseURL: API_BASE_PATH,
    url: `/module/${modulePath}`,
    data,
  });
}

export function* pinPostOnFeedSaga(action) {
  const isNewPostsFeed = yield select(flagEnabled, 'posts_feed_module');
  if (isNewPostsFeed) {
    return;
  }
  const {
    payload: { item },
  } = action;
  const { username } = yield select(getCurrentUser);
  const moduleId = yield select(
    getModuleIdByModuleType,
    ModuleType.FEED_PINNED_DEVIATION
  );
  if (!moduleId) {
    ddt.error(
      'feed',
      '/init/posts under profile_posts_feed feature does not seem to return the pinned feed module'
    );
    return;
  }

  let result;
  if (item !== undefined) {
    result = yield call(postRequest, 'feed_pinned_deviation/pin', {
      itemid: item.deviationId,
      typeid: item.typeId,
      moduleid: moduleId,
      username,
    });
  } else {
    result = yield call(postRequest, 'feed_pinned_deviation/unpin', {
      moduleid: moduleId,
      username,
    });
  }

  if (result && result.success) {
    yield put(
      updateModule({
        id: moduleId,
        moduleData: {
          itemId: item && item.deviationId,
          typeId: item && item.typeId,
        },
      })
    );
    return;
  }
}

export function* newPinPostOnFeedSaga(action) {
  const isNewPostsFeed = yield select(flagEnabled, 'posts_feed_module');
  if (!isNewPostsFeed) {
    return;
  }
  const {
    payload: { item },
  } = action;
  const { username } = yield select(getCurrentUser);

  const section = yield select(getActiveSection);
  // Both FEED and POSTS_FEED use the same pinning/unpinning logic
  let moduleType;
  switch (section) {
    case 'home':
      moduleType = ModuleType.FEED;
      break;
    case 'posts':
      moduleType = ModuleType.POSTS_FEED;
      break;
    default:
      return;
  }
  const moduleId = yield select(getModuleIdByModuleType, moduleType);
  if (!moduleId) {
    return;
  }

  let response;
  if (item !== undefined) {
    response = yield call(postRequest, `${moduleType}/pin`, {
      itemid: item.deviationId,
      typeid: item.typeId,
      moduleid: moduleId,
      username,
    });
  } else {
    response = yield call(postRequest, `${moduleType}/unpin`, {
      moduleid: moduleId,
      username,
    });
  }
  if (response && response.success && response.module) {
    yield runModuleInitActions(response.module);
  } else {
    ddt.error('feed', 'Could not unpin item');
  }
}

export function* featurePostSaga(action) {
  const {
    payload: { item },
  } = action;
  const { username } = yield select(getCurrentUser);
  const moduleId = yield select(
    getModuleIdByModuleType,
    ModuleType.FEATURED_POSTS
  );

  const response = yield call(postRequest, 'featured_posts/feature', {
    moduleid: moduleId,
    itemid: item.deviationId,
    typeid: item.typeId,
    username,
  });

  if (response && response.success) {
    const { result, entities } = normalize(item, deviationSchema);
    yield put(
      withOffset.actions.insertItems({
        streamId: FEATURED_POSTS_IDS_STREAM,
        items: [result],
        entities,
        offset: 0,
      })
    );
  }
}

export function* unfeaturePostSaga(action) {
  const {
    payload: { item },
  } = action;
  const { username } = yield select(getCurrentUser);
  const moduleId = yield select(
    getModuleIdByModuleType,
    ModuleType.FEATURED_POSTS
  );

  const response = yield call(postRequest, 'featured_posts/unfeature', {
    moduleid: moduleId,
    itemid: item.deviationId,
    typeid: item.typeId,
    username,
  });

  if (response && response.success) {
    const { result } = normalize(item, deviationSchema);
    yield put(
      withOffset.actions.deleteItem({
        streamId: FEATURED_POSTS_IDS_STREAM,
        item: result,
      })
    );
  }
}

function* updateDeviationEntity(deviation) {
  const { result, entities } = normalize(deviation, deviationSchema);
  yield put(putEntities({ entities }));
  return { result, entities };
}

function* getFirstStreamDeviation(streamId) {
  const [firstItemId] = yield select(baseSelectors.getItems, streamId);
  const firstItem = yield select(
    getDataEntityById,
    deviationSchema,
    firstItemId
  );
  return firstItem;
}

export function* pinPostSaga(action) {
  const {
    payload: { streamId, item },
  } = action;
  const { username } = yield select(getCurrentUser);
  const moduleId = yield select(getModuleIdByModuleType, ModuleType.POSTS_FEED);

  const response = yield call(postRequest, 'posts_feed/pin', {
    moduleid: moduleId,
    itemid: item.deviationId,
    typeid: item.typeId,
    username,
  });

  if (response && response.success) {
    const firstItem = yield call(getFirstStreamDeviation, streamId);
    if (firstItem && firstItem.isPinned) {
      yield call(updateDeviationEntity, { ...firstItem, isPinned: false });
    }
    const { result } = yield call(updateDeviationEntity, {
      ...item,
      isPinned: true,
    });
    yield put(
      withOffset.actions.insertItems({
        streamId,
        items: [result],
        offset: 0,
      })
    );
  }
}

export function* unpinPostSaga(action) {
  const {
    payload: { streamId },
  } = action;
  const { username } = yield select(getCurrentUser);
  const moduleId = yield select(getModuleIdByModuleType, ModuleType.POSTS_FEED);

  const firstItem = yield call(getFirstStreamDeviation, streamId);
  if (!firstItem || !firstItem.isPinned) {
    return;
  }

  const response = yield call(postRequest, 'posts_feed/unpin', {
    moduleid: moduleId,
    username,
  });

  if (response && response.success) {
    const { result } = yield call(updateDeviationEntity, {
      ...firstItem,
      isPinned: false,
    });
    const [firstItemId, ...rest] = yield select(
      baseSelectors.getItems,
      streamId
    );
    const offset = rest.findIndex(id => id === firstItemId);
    yield put(
      withOffset.actions.deleteItem({
        streamId,
        item: result,
      })
    );
    yield put(
      withOffset.actions.insertItems({
        streamId,
        items: [result],
        offset,
      })
    );
  }
}

export default function* rootFeedSaga() {
  yield all([
    takeLatest(pinPostOnFeed, pinPostOnFeedSaga),
    takeLatest(pinPostOnFeed, newPinPostOnFeedSaga),
    takeLatest(pinPost, pinPostSaga),
    takeLatest(unpinPost, unpinPostSaga),
    takeLatest(featurePost, featurePostSaga),
    takeLatest(unfeaturePost, unfeaturePostSaga),
  ]);
}
